import Utils from "../basic/Utils";
import DitapPaneManager from "../popup/DitapPaneManager";

/**
 * 다이탭 패널 최상위 클래스.<br>
 * Ditap panel top class.
 * @alias DitapPanel
 *
 * @constructor
 * @param {Element} container 패널 컨테이너 요소.<br> A panel container element.
 * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
 *
 *
 * @example
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 * const ditapPanelElement = document.createElement("div");
 *
 *
 * ditapPanelElement.classList.add("ditap-panel");
 * document.body.append(ditapPanelElement);
 *
 * const panel = new Ditap.DitapPanel(ditapPanelElement, viewer);
 *
 * const content1 = document.createElement("p");
 * content1.innerText = "다이탭 패널에 대한 내용1";
 *
 * const content2 = document.createElement("p");
 * content2.innerText = "다이탭 패널에 대한 내용2";
 *
 * panel.open(panel.entireWrapper)
 * panel.setTitle("제목을 입력하세요.");
 * panel.setSubtitle("부제목을 입력하세요.");
 *
 * panel.appendContentWrapper(content1);
 * panel.appendContentWrapper(content2);
 *
 */
class DitapPanel {
  constructor(container, viewer) {
    if (container instanceof Element) {
      this._container = container;
    } else {
      throw Error("DitapPanel: 'container' is required.");
    }
    this._ditapPaneManager = undefined;
    if (viewer.ditapPaneManager instanceof DitapPaneManager) {
      this._ditapPaneManager = viewer.ditapPaneManager;
    } else {
      this._ditapPaneManager = undefined;
      // throw Error("DitapPopup manager type error");
    }
    this._entireWrapper = undefined;
    this._headerWrapper = undefined;
    this._contentWrapper = undefined;
    this._header = undefined;
    this._title = undefined;
    this._subtitle = undefined;
    this._closeBtn = undefined;
    this._fullBtn = undefined;
    this._ditapPanelManager = viewer.ditapPanelManager;
    this._ditapPanelManager._ditapPaneManager = this._ditapPaneManager;
    this.isMobile = Utils.isBodyWidthLessThan600();

    // 판넬 클래스 추가
    if (!container.classList.contains("ditap-panel")) {
      container.classList.add("ditap-panel");
    }
    this.init(); // 엘리먼트 엘리먼트 생성

    if (this._ditapPaneManager && this.isMobile) {
      const isTouchDevice = "ontouchstart" in document.documentElement;
      if (isTouchDevice) {
        this.createSwiperEvent();
      } else {
        this.createPanelBtnEvent();
      }
    }

    this.changeCloseBtn();
  }

  /**
   * 패널이 삽입될 컨테이너 HTML 요소.<br>
   * Container HTML element where the panel will be inserted.
   * @type {Element}
   */
  get container() {
    return this._container;
  }
  set container(container) {
    if (container instanceof Element) {
      this._container = container;
    }
  }

  /**
   * 패널 헤더 영역을 감싸는 요소.<br>
   * Panel headerWrapper element.
   * @type {Element}
   */
  get headerWrapper() {
    return this._headerWrapper;
  }
  set headerWrapper(headerWrapper) {
    this._headerWrapper = headerWrapper;
  }

  /**
   * 패널 컨텐츠 영역을 감싸는 요소.<br>
   * Panel contentWrapper element
   * @type {Element}
   */
  get contentWrapper() {
    return this._contentWrapper;
  }
  set contentWrapper(contentWrapper) {
    this._contentWrapper = contentWrapper;
  }

  /**
   * 패널 헤더 영역 요소.<br>
   * Panel header element
   * @type {Element}
   */
  get header() {
    return this._header;
  }
  set header(header) {
    this._header = header;
  }

  /**
   * 패널 제목 영역 요소.<br>
   * Panel title element.
   * @type {Element}
   */
  get title() {
    return this._title;
  }
  set title(title) {
    this._title = title;
  }

  /**
   * 패널 부제목 영역 요소.<br>
   * Panel subtitle element
   * @type {Element}
   */
  get subtitle() {
    return this._subtitle;
  }
  set subtitle(subtitle) {
    this._subtitle = subtitle;
  }

  /**
   * 패널 취소 버튼 요소.<br>
   * Panel close button element.
   * @type {Element}
   */
  get closeBtn() {
    return this._closeBtn;
  }
  set closeBtn(closeBtn) {
    this._closeBtn = closeBtn;
  }

  /**
   * 패널 전체 화면 버튼 요소.<br>
   * Panel toggle full button element.
   * @type {Element}
   */
  get fullBtn() {
    return this._fullBtn;
  }
  set fullBtn(fullBtn) {
    this._fullBtn = fullBtn;
  }

  /**
   * 다이탭 패널을 관리하기 위한 DitappanelManager.<br>
   * ditapPanelManager to manage DitapPanel.
   * @type {Element}
   */
  get ditapPanelManager() {
    return this._ditapPanelManager;
  }
  set ditapPanelManager(ditapPanelManager) {
    this._ditapPanelManager = ditapPanelManager;
  }

  /*
   * DitapPopup/DitapPanel을 관리하는 클래스.<br>
   * The class that manages the DitapPopup/DitapPanel.
   * @type {DitapPaneManager}
   */
  set ditapPaneManager(ditapPaneManager) {
    this._ditapPaneManager = ditapPaneManager;
  }
  get ditapPaneManager() {
    return this._ditapPaneManager;
  }

  /**
   * 패널 초기화.<br>
   * Panel init.
   */
  init() {
    // 엘리먼트 생성
    const entireWrapper = document.createElement("div");
    entireWrapper.classList.add(
      "ditap-entire-wrap",
      "hide",
      "ditap-panel-property"
    );

    const headerWrapper = document.createElement("div");
    headerWrapper.classList.add("ditap-panel-title-wrap");

    const contentWrapper = document.createElement("div");
    contentWrapper.classList.add("ditap-panel-content-wrap");

    const header = document.createElement("div");
    header.classList.add("ditap-popup-header");
    const title = document.createElement("h2");
    title.classList.add("ditap-panel-title");

    const closeBtn = document.createElement("button");
    closeBtn.classList.add("ditap-popup-close-btn");
    const fullBtn = document.createElement("button");
    fullBtn.classList.add("ditap-popup-full-btn", "ditap-custom-button");
    const btnWrap = document.createElement("div");
    btnWrap.classList.add("ditap-panel-btn-wrap");
    const subtitle = document.createElement("p");
    subtitle.classList.add("ditap-panel-subtitle");

    const leftButton = document.createElement("button");
    leftButton.classList.add(
      "ditap-direction-btn",
      "ditap-custom-button",
      "ditap-popup-left",
      "disabled",
      "ditap-custom-button"
    );
    leftButton.disabled = true;

    const rightButton = document.createElement("button");
    rightButton.classList.add(
      "ditap-direction-btn",
      "ditap-custom-button",
      "ditap-popup-right",
      "disabled",
      "ditap-custom-button"
    );
    rightButton.disabled = true;

    const headerTop = document.createElement("div");
    headerTop.classList.add("ditap-popup-top");

    btnWrap.append(fullBtn);
    btnWrap.append(closeBtn);

    // headerWrapper 안에 추가
    header.append(title);
    header.append(leftButton);
    header.append(rightButton);
    header.append(btnWrap);

    headerWrapper.append(header);
    headerWrapper.append(subtitle);

    entireWrapper.append(headerWrapper);
    entireWrapper.append(contentWrapper);
    // 부모 자식으로 headerWrapper 추가
    if (this.container instanceof Element) {
      this.container.append(entireWrapper);
    }

    entireWrapper.addEventListener("mouseover", Utils.hideLabelElement);
    entireWrapper.addEventListener("mouseout", Utils.showLabelElement);

    // set
    this.entireWrapper = entireWrapper;
    this.headerWrapper = headerWrapper;
    this.contentWrapper = contentWrapper;
    this.headerTop = headerTop;
    this.header = header;
    this.title = title;
    this.subtitle = subtitle;
    this.closeBtn = closeBtn;
    this.fullBtn = fullBtn;
    this.btnWrap = btnWrap;
    this.leftButton = leftButton;
    this.rightButton = rightButton;

    // 헤더 닫기 버튼
    this.closeBtn.addEventListener("click", () => {
      this.close();
    });

    this.fullBtn.addEventListener(
      "click",
      (this.toggleExpandPanel = () => {
        if (this.container.classList.contains("expanded")) {
          this.container.classList.remove("expanded");
        } else {
          this.container.classList.add("expanded");
        }
      })
    );
  }

  /**
   * 이 객체의 모든 리소스 제거.</br>
   * Remove all resources of this object.
   */
  destroy() {
    if (this.entireWrapper instanceof Element) {
      this.entireWrapper.remove();
    }
    if (this.headerWrapper instanceof Element) {
      this.headerWrapper.remove();
    }
    if (this.contentWrapper instanceof Element) {
      this.contentWrapper.remove();
    }
    if (this.closeBtn instanceof Element) {
      this.closeBtn.removeEventListener("click", () => {
        this.close();
      });
    }
    if (this.fullBtn instanceof Element) {
      this.fullBtn.removeEventListener("click", this.toggleExpandPanel);
    }

    if (this.ditapPanelManager.openPanel === this) {
      this.ditapPanelManager.setOpenPanel(undefined);
    }
    if (this.entireWrapper instanceof Element) {
      this.entireWrapper.removeEventListener(
        "mouseover",
        Utils.hideLabelElement
      );
      this.entireWrapper.removeEventListener(
        "mouseover",
        Utils.showLabelElement
      );
    }

    this.removePanelBtnEvent();
    this.removeSwiperEvent();
  }

  /**
   * 제목 설정.<br>
   * Set the title.
   * @param {String} title 제목.<br> Title to set.
   */
  setTitle(title) {
    this.title.innerText = title;
  }

  /**
   * 부제목 설정.<br>
   * Set the subtitle.
   * @param {String} title 제목.<br>  Title to set
   */
  setSubtitle(title) {
    this.subtitle.innerText = title;
  }

  /**
   * 헤더 영역에 요소 추가.<br>
   * Append element to HeaderWrapper.
   * @param {Element} element 추가할 요소.<br> Element to add.
   */
  appendHeaderWrapper(element) {
    if (element instanceof Element) {
      this.headerWrapper.append(element);
    }
  }

  /**
   * 헤더 영역 맨 앞에 요소 추가.<br>
   * Prepend element to HeaderWrapper.
   * @param {Element} element 추가할 요소.<br> Element to add.
   */
  prependHeaderWrapper(element) {
    if (element instanceof Element) {
      this.headerWrapper.prepend(element);
    }
  }

  /**
   * 헤더 영역의 자식 요소 제거.<br>
   * Remove children of HeaderWrapper
   * @param {Element} element 제거할 요소.<br. Element to remove.
   */
  removeHeaderWrapper(element) {
    if (element instanceof Element) {
      this.headerWrapper.removeChild(element);
    }
  }

  /**
   * 헤더 영역의 모든 자식 요소 제거.<br>
   * Remove all children of HeaderWrapper.
   */
  removeAllHeaderWrapper() {
    this.headerWrapper.innerHtml = "";
  }

  /**
   * 컨텐츠 영역에 요소 추가.<br>
   * Append element to ContentWrapper
   * @param {Element} element 추가할 요소.<br> element to add.
   */
  appendContentWrapper(element) {
    if (element instanceof Element) {
      this.contentWrapper.append(element);
    }
  }

  /**
   * 컨텐츠 영역 맨 앞에 요소 추가.<br>
   * Prepend element to ContentWrapper.
   * @param {Element} element 추가할 요소.<br> element to add.
   */
  prependContentWrapper(element) {
    if (element instanceof Element) {
      this.contentWrapper.prepend(element);
    }
  }

  /**
   * 컨텐츠 영역의 자식 요소 제거.</br>
   * Remove children of ContentWrapper.
   * @param {Element} element 제거할 요소.<br>Element to remove.
   */
  removeContentWrapper(element) {
    if (element instanceof Element) {
      this.contentWrapper.removeChild(element);
    }
  }

  /**
   * 컨텐츠 영역의 모든 자식 요소 제거.</br>
   * Remove all children of ContentWrapper
   */
  removeAllContentWrapper() {
    this.contentWrapper.innerHtml = "";
  }

  /*
   * 닫기 버튼 위치 변경.<br>
   */
  changeCloseBtn() {
    const isMobile = Utils.isBodyWidthLessThan600();
    if (this.entireWrapper.classList.contains("header-top")) {
      if (isMobile) {
        this.headerTop.append(this.btnWrap);
        this.entireWrapper.prepend(this.headerTop);
      } else {
        this.header.append(this.btnWrap);
      }
    }
  }

  /**
   * 패널 요소 또는 컨테이너 열기.<br>
   * Open panel Element or Container.
   * @param {Element} element 매개변수가 없는 경우 wrapper 오픈.<br> If no parameter is entered, open headerWrapper
   */
  open(element) {
    if (element) {
      // open element
      element.classList.remove("hide");
    } else {
      this.ditapPanelManager.setOpenPanel(this);

      // open wrapper
      this.open(this.entireWrapper);

      // open container
      this.container.classList.remove("hide");
      if (!this.viewer.container.classList.contains("ditap-viewer")) {
        this.viewer.container.classList.add("ditap-viewer");
      }

      if (!this.viewer.container.classList.contains("ditap-panel-open")) {
        this.viewer.container.classList.add("ditap-panel-open");
        this.viewer.resize();
      }

      // 도움말 라벨 위치 변경
      const mobileLabel = this.viewer.container.getElementsByClassName(
        "ditap-mobile-label"
      )[0];

      if (mobileLabel) {
        if (this.viewer.container.querySelector(".ditap-search")) {
          mobileLabel.classList.add("bottom-2");
        } else {
          mobileLabel.classList.add("bottom-1");
        }
      }

      this.ditapPaneManager?.addPane(this.entireWrapper);
      this.createResizeEvent();
    }
  }

  /**
   * 패널 닫기.<br>
   * Close panel.
   * @param {Element} element 매개변수가 없는 경우 wrapper 오픈.<br> If no parameter is entered, open headerWrapper
   */
  close(element = undefined) {
    if (element) {
      element.classList.add("hide");
    } else {
      // close container
      this.container.classList.add("hide");

      // close wrapper
      this.entireWrapper.classList.add("hide");
      this.ditapPaneManager?.removePane(this.entireWrapper);

      if (this.ditapPanelManager.openPanel === this) {
        this.ditapPanelManager.setOpenPanel(undefined);
      }

      if (this.viewer.container.classList.contains("ditap-panel-open")) {
        this.viewer.container.classList.remove("ditap-panel-open");
        this.viewer.resize();
      }

      // 도움말 라벨 위치 변경
      const mobileLabel = this.viewer.container.getElementsByClassName(
        "ditap-mobile-label"
      )[0];

      mobileLabel?.classList.remove("bottom-2");
      mobileLabel?.classList.remove("bottom-1");
    }
  }

  /**
   * Ditap Panel 버튼 이벤트 추가.<br>
   */
  createPanelBtnEvent() {
    this.leftButton.addEventListener(
      "click",
      (this.leftBtnEvent = () => {
        this.ditapPaneManager.prevPane();
      })
    );

    this.rightButton.addEventListener(
      "click",
      (this.rightBtnEvent = () => {
        this.ditapPaneManager.nextPane();
      })
    );
  }

  /**
   * Ditap Panel 버튼 이벤트 제거.<br>
   * Remove panel button event.
   */
  removePanelBtnEvent() {
    if (this.leftBtnEvent) {
      this.leftButton.removeEventListener("click", this.leftBtnEvent);
    }
    if (this.rightBtnEvent) {
      this.rightButton.removeEventListener("click", this.rightBtnEvent);
    }
  }

  /**
   * Ditap Panel 헤더 영역에 스와이퍼 이벤트 추가.<br>
   * Add swiper event to the panel header area.
   */
  createSwiperEvent() {
    let startX = 0;
    let moveX = 0;

    this.header.addEventListener(
      "touchstart",
      (this.touchstartEvent = (event) => {
        // 터치 시작 지점 저장
        startX = event.touches[0].clientX;
      })
    );

    this.header.addEventListener(
      "touchmove",
      (this.touchmoveEvent = (event) => {
        // 터치 이동 지점 저장
        moveX = event.touches[0].clientX;
      })
    );

    this.header.addEventListener(
      "touchend",
      (this.touchendEvent = () => {
        // 터치 이동 거리 계산
        const diffX = moveX - startX;

        if (Utils.isBodyWidthLessThan600()) {
          if (diffX > 0) {
            this.ditapPaneManager.prevPane();
          } else {
            this.ditapPaneManager.nextPane();
          }
        }
      })
    );
  }

  /**
   * Ditap Panel 헤더 영역에 스와이퍼 이벤트 제거.<br>
   * Remove swiper event to the panel header area.
   */
  removeSwiperEvent() {
    if (this.touchstartEvent) {
      this.header.removeEventListener("touchstart", this.touchstartEvent);
    }
    if (this.touchmoveEvent) {
      this.header.removeEventListener("touchmove", this.touchmoveEvent);
    }
    if (this.touchendEvent) {
      this.header.removeEventListener("touchend", this.touchendEvent);
    }
  }

  /*
   * 윈도우 크기 조절 시 이벤트 생성.</br>
   * Create an event for window resize.
   */
  createResizeEvent() {
    window.addEventListener(
      "resize",
      (this.resizeEvent = () => {
        const currentIsMobile = Utils.isBodyWidthLessThan600();

        if (this.isMobile !== currentIsMobile) {
          this.isMobile = currentIsMobile;
          this.changeCloseBtn();

          if (this.isMobile) {
            const isTouchDevice = "ontouchstart" in document.documentElement;
            if (isTouchDevice) {
              this.createSwiperEvent();
            } else {
              this.createPanelBtnEvent();
            }
          }
        } else if (!currentIsMobile) {
          this.removePanelBtnEvent();
          this.removeSwiperEvent();
        }
      })
    );
  }
}

export default DitapPanel;
